import React, { useState, useEffect } from 'react';
import '../App.css';
import '../assets/Analytics.css';
import { analyticsApi, Analytics as AnalyticsData } from '../utils/api';
import logoIcon from '../assets/logo/icon.svg';
import { useData } from '../context/DataContext';

export const Analytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { orders, drivers, stores } = useData();

  useEffect(() => {
    (async () => {
      try {
        setError(null);
        setData(await analyticsApi.get());
      } catch (err: any) {
        setError(err.message || 'Failed to load analytics');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  /* Prefer the server's counts — they are over the whole table, while the
     orders list here is only what the list endpoint returned. */
  const active = data?.active_orders ?? orders.filter(o => o.status !== 'delivered').length;
  const delivered = data?.delivered_orders ?? orders.filter(o => o.status === 'delivered').length;
  const noLocation =
    data?.orders_missing_location ??
    orders.filter(o => o.latitude == null || o.longitude == null).length;

  return (
    <div className="page">
      <header className="page__head">
        <img src={logoIcon} alt="" className="page__logo"
             onError={e => { e.currentTarget.style.display = 'none'; }} />
        <div className="page__titles">
          <h1 className="page__title">Analytics</h1>
          <p className="page__sub">Overview of orders and fleet</p>
        </div>
      </header>

      {isLoading ? (
        <div className="stat-grid">
          {[0, 1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 104 }} />)}
        </div>
      ) : error ? (
        <div className="state state--error">
          <p className="state__title">Couldn’t load analytics</p>
          <p className="state__body">{error}</p>
        </div>
      ) : (
        <div className="stat-grid">
          <div className="stat stat--accent">
            <p className="stat__label">Total orders</p>
            <p className="stat__value">{data?.total_orders ?? orders.length}</p>
            <p className="stat__note">All time</p>
          </div>
          <div className="stat">
            <p className="stat__label">Active</p>
            <p className="stat__value">{active}</p>
            <p className="stat__note">Awaiting delivery</p>
          </div>
          <div className="stat">
            <p className="stat__label">Delivered</p>
            <p className="stat__value">{delivered}</p>
            <p className="stat__note">Completed</p>
          </div>
          <div className="stat">
            <p className="stat__label">Missing location</p>
            <p className="stat__value">{noLocation}</p>
            <p className="stat__note">Can’t be routed</p>
          </div>
          <div className="stat">
            <p className="stat__label">Drivers</p>
            <p className="stat__value">{drivers.length}</p>
            <p className="stat__note">On the roster</p>
          </div>
          <div className="stat">
            <p className="stat__label">Shops</p>
            <p className="stat__value">{stores.length}</p>
            <p className="stat__note">Registered</p>
          </div>
        </div>
      )}
    </div>
  );
};
